import { AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';

import { map, Observable, take } from 'rxjs';

import { Airport } from '@fcom/common/interfaces/booking/multi-city-state.interface';
import { BookingLocationCodes } from '@fcom/common/interfaces/booking/booking-flow.interface';
import { formatTime } from '@fcom/common/pipes/time.pipe';
import { ScrollHandleContainerComponent } from '@fcom/common/components/scroll-handle-container/scroll-handle-container.component';
import { Step } from '@fcom/common/interfaces/step.interface';
import { TripType } from '@fcom/core/interfaces/triptype.interface';
import { RAIL_AND_FLY_LOCATION_CODES, RootPaths } from '@fcom/core/constants';
import { GlobalBookingFlight, LocationPair } from '@fcom/common/store/store.interface';
import { LoginGender } from '@fcom/core-api/login/interfaces/login.interface';
import { Category } from '@fcom/dapi/api/models/category';
import { FinnairAmount } from '@fcom/dapi/api/models/finnair-amount';
import { FinnairBoundGroup } from '@fcom/dapi/api/models/finnair-bound-group';
import { FinnairBoundItem } from '@fcom/dapi/api/models/finnair-bound-item';
import { FinnairCabinClass } from '@fcom/dapi/api/models/finnair-cabin-class';
import { FinnairCart } from '@fcom/dapi/api/models/finnair-cart';
import { FinnairDisruptedBoundItem } from '@fcom/dapi/api/models/finnair-disrupted-bound-item';
import { FinnairGender } from '@fcom/dapi/api/models/finnair-gender';
import { FinnairItineraryItem } from '@fcom/dapi/api/models/finnair-itinerary-item';
import { FinnairItineraryItemFlight } from '@fcom/dapi/api/models/finnair-itinerary-item-flight';
import { FinnairItineraryItemFlightStatus } from '@fcom/dapi/api/models/finnair-itinerary-item-flight-status';
import { FinnairItineraryItemType } from '@fcom/dapi/api/models/finnair-itinerary-item-type';
import { FinnairLocation } from '@fcom/dapi/api/models/finnair-location';
import { FinnairLowestPrice } from '@fcom/dapi/api/models/finnair-lowest-price';
import { FinnairOrder } from '@fcom/dapi/api/models/finnair-order';
import { FinnairOtherInformation } from '@fcom/dapi/api/models/finnair-other-information';
import { FinnairPassengerCode } from '@fcom/dapi/api/models/finnair-passenger-code';
import { FinnairPassengerItem } from '@fcom/dapi/api/models/finnair-passenger-item';
import { FinnairPassengerServiceItem } from '@fcom/dapi/api/models/finnair-passenger-service-item';
import { FinnairPassengerServiceSelectionItem } from '@fcom/dapi/api/models/finnair-passenger-service-selection-item';
import { FinnairServiceBoundItem } from '@fcom/dapi/api/models/finnair-service-bound-item';
import { FinnairServiceCatalogEligibilityCategory } from '@fcom/dapi/api/models/finnair-service-catalog-eligibility-category';
import { FinnairServiceCatalogItemV3 } from '@fcom/dapi/api/models/finnair-service-catalog-item-v-3';
import { FinnairServiceCatalogV3 } from '@fcom/dapi/api/models/finnair-service-catalog-v-3';
import { FinnairServiceItem } from '@fcom/dapi/api/models/finnair-service-item';
import { FinnairServices } from '@fcom/dapi/api/models/finnair-services';
import { FinnairServiceSegmentItem } from '@fcom/dapi/api/models/finnair-service-segment-item';
import { FinnairTotalPricesDetails } from '@fcom/dapi/api/models/finnair-total-prices-details';
import { FinnairTravelEndpoint } from '@fcom/dapi/api/models/finnair-travel-endpoint';
import { ItineraryItem } from '@fcom/dapi/api/models/itinerary-item';
import { ItineraryItemFlight } from '@fcom/dapi/api/models/itinerary-item-flight';
import { ItineraryItemLayover } from '@fcom/dapi/api/models/itinerary-item-layover';
import { SubCategory } from '@fcom/dapi/api/models/sub-category';
import { Amount, LegEndpoint } from '@fcom/dapi/interfaces/dapi.interface';
import {
  CustomServiceType,
  FinnairServiceItemWithUpsell,
  GroupedServices,
  ServiceStatus,
  Upsell,
} from '@fcom/dapi/interfaces/services.interface';
import { DapiHttpErrorResponse } from '@fcom/dapi/interfaces/http.interface';
import { PaxDetailsFormData } from '@fcom/dapi/interfaces/pax-details.interface';
import { DateFormat, DateTranslations } from '@fcom/core/utils/date.format';
import { groupBy, isEmptyObject, isFinnairNorraOrWetLease, isPresent, rangeFrom } from '@fcom/core/utils/utils';
import { LocalDate, TzDate } from '@fcom/core/utils/tz-date';
import { matchesLastPathSegment, pathIsWithinHardcodedPath } from '@fcom/core/utils/app-url-utils';
import { Bound } from '@fcom/dapi/api/models/bound';
import { BOOKING_STEPS } from '@fcom/common/config/booking-config';
import { TranslatedCountryCode } from '@fcom/common/services/phone-prefix/phone-prefix.service';
import { getIconForServiceCategory } from '@fcom/dapi/utils/service-category-utils';
import { Airline } from '@fcom/common/interfaces/airlines';

import { OrderPartStatus } from '../store/store.interface';
import {
  BUS_AIRCRAFT_CODE,
  GENDER_OPTIONS,
  Title,
  TRAIN_AIRCRAFT_CODE,
  WIDE_BODY_AIRCRAFT_CODES,
} from '../interfaces/utils.interface';
import { CartOrOrder } from '../interfaces/cart-or-order.interface';
import { FinnairBoundItemWithLocation } from '../interfaces/flight-selection.interface';
import { FinnairServiceItemWithIcon } from '../interfaces/booking-summary.interface';
import { combineServices, getPassengerTierLevel } from '../modules/ancillaries/utils/ancillary.utils';
import { isBoundBasedCategory, isJourneyBasedCategory } from '../modules/ancillaries/utils/category.utils';
import { isFlight } from './flight.utils';
import { getCategoryLevelQuotaCount } from '../modules/ancillaries/utils/quota.utils';

export const toAmount = (amount: { amount?: string | number; currencyCode?: string } | null | undefined): Amount => ({
  amount: isPresent(amount?.amount) ? String(amount.amount) : amount?.amount,
  currencyCode: amount?.currencyCode,
});

export const isInBookingPurchase = (url: string): boolean =>
  pathIsWithinHardcodedPath(url, RootPaths.BOOKING_ROOT) &&
  (matchesLastPathSegment(url, BOOKING_STEPS.PURCHASE_SUCCESS.path) ||
    matchesLastPathSegment(url, BOOKING_STEPS.CHECKOUT.path));

export const isInMmb = (url: string): boolean =>
  pathIsWithinHardcodedPath(url, RootPaths.MANAGE_BOOKING_ROOT) &&
  !matchesLastPathSegment(url, RootPaths.MANAGE_BOOKING_ROOT);

export const isInCorporatePortal = (url: string): boolean => pathIsWithinHardcodedPath(url, RootPaths.CORPORATE);

export const isTravelReady = (url: string): boolean => pathIsWithinHardcodedPath(url, RootPaths.TRAVEL_READY);

export const isMmbStep = (url: string, step: Step): boolean =>
  pathIsWithinHardcodedPath(url, RootPaths.MANAGE_BOOKING_ROOT) && matchesLastPathSegment(url, step.path);

export const isChangeFlowStep = (url: string, step: Step): boolean =>
  url.includes([RootPaths.VOLUNTARY_CHANGE_ROOT, step.path].join('/'));

export const isDisrupted = (order: FinnairOrder): boolean => {
  return (
    order?.bounds.some((bound) => isDisruptedBound(bound) && !isBoundFlown(bound.disruptedBound.disruptedBound)) ??
    false
  );
};

export const isDisruptedBound = (bound: FinnairBoundItem | undefined): boolean => {
  return !!bound?.disruptedBound?.disruptedBound;
};

export const isBoundFlown = (bound: FinnairBoundItem | FinnairDisruptedBoundItem): boolean =>
  allFlightsHaveStatuses(bound, [FinnairItineraryItemFlightStatus.FLOWN]);

export const isBoundOrFlightConfirmedOrCheckedIn = (
  boundOrFlight: FinnairBoundItem | FinnairItineraryItemFlight
): boolean => {
  const confirmedOrCheckedIn = [
    FinnairItineraryItemFlightStatus.CONFIRMED,
    FinnairItineraryItemFlightStatus.CHECKED_IN,
  ];
  return isBound(boundOrFlight)
    ? allFlightsHaveStatuses(boundOrFlight, confirmedOrCheckedIn)
    : confirmedOrCheckedIn.includes(boundOrFlight.status);
};

const allFlightsHaveStatuses = (
  bound: FinnairBoundItem | FinnairDisruptedBoundItem,
  statuses: FinnairItineraryItemFlightStatus[]
): boolean => bound.itinerary.filter(isFlight).every((i) => statuses.includes(i.status));

export const isBound = (
  toBeDetermined: FinnairBoundItem | FinnairDisruptedBoundItem | FinnairItineraryItemFlight | Bound
): toBeDetermined is FinnairBoundItem | FinnairDisruptedBoundItem | Bound =>
  toBeDetermined && !!(toBeDetermined as FinnairBoundItem | FinnairDisruptedBoundItem | Bound).itinerary;

const asTimeAndWeekDay = (dateTime: string, translations: unknown): string =>
  `${formatTime(dateTime)} ${new DateFormat(translations as DateTranslations).format(dateTime, DateFormat.WEEKDAY_SHORT)}`;

export const legEndpointToUfoAirport = (
  legEndpoint: LegEndpoint | FinnairTravelEndpoint,
  translations: unknown,
  locations?: Record<string, FinnairLocation>
): Airport => {
  if (!legEndpoint) {
    return undefined;
  }
  const { dateTime, locationCode } = legEndpoint;
  const location = locations?.[locationCode];
  return {
    dateTime: asTimeAndWeekDay(dateTime, translations),
    airportCode: locationCode,
    city: location?.cityName ?? (legEndpoint as LegEndpoint).cityName,
    airport: location?.name ?? (legEndpoint as LegEndpoint).name,
  };
};

/**
 * Only feasible for cart model checking
 * @param totalPrices
 */
export const isAwardBooking = (totalPrices: FinnairTotalPricesDetails): boolean =>
  !!totalPrices?.total?.total?.totalPoints;

export const numberOfDaysInMonth = (year: number): number[] => {
  return rangeFrom(1, 12).map((m) => LocalDate.getAmountOfDaysInMonth(year, m));
};

export const getAircraftTailForFlight = (flight: FinnairItineraryItemFlight): string => {
  if (isBus(flight)) {
    return BUS_AIRCRAFT_CODE.toLowerCase();
  }

  if (isTrain(flight)) {
    return TRAIN_AIRCRAFT_CODE.toLowerCase();
  }

  if (flight.operatingAirline?.name === 'Iberia for Finnair') {
    return 'IB';
  }

  return flight.operatingAirline?.code;
};

export const isBus = (flight: FinnairItineraryItemFlight): boolean =>
  flight?.aircraft?.code?.toUpperCase() === BUS_AIRCRAFT_CODE;

export const isTrain = (flight: FinnairItineraryItemFlight): boolean =>
  flight?.aircraft?.code?.toUpperCase() === TRAIN_AIRCRAFT_CODE;

export const isBusOrTrain = (flight: FinnairItineraryItemFlight): boolean => isBus(flight) || isTrain(flight);

export const hasWideBodyAircraftCode = (code?: string): boolean => WIDE_BODY_AIRCRAFT_CODES.includes(code);

export const isWideBodyFlight = (flight: FinnairItineraryItemFlight): boolean =>
  hasWideBodyAircraftCode(flight?.aircraft?.code);

export const hasWideBodyFlight = (itinerary: Array<FinnairItineraryItem | ItineraryItem>): boolean =>
  (itinerary ?? []).filter(isFlight).some(isWideBodyFlight);

export const getTails = (
  boundOrFlight: FinnairBoundItem | FinnairDisruptedBoundItem | FinnairItineraryItemFlight
): string[] => {
  const tails: string[] = isBound(boundOrFlight)
    ? boundOrFlight.itinerary
        .filter(isFlight)
        .flatMap((itinerary: FinnairItineraryItemFlight) => getAircraftTailForFlight(itinerary))
    : [getAircraftTailForFlight(boundOrFlight)];
  return tails.filter((tail) => tail);
};

const checkBoundsOrSegmentsAllFlightsMatchesWithFn = (
  boundOrFlight: FinnairBoundItem | FinnairDisruptedBoundItem | FinnairItineraryItemFlight,
  fn: (flight: FinnairItineraryItemFlight) => boolean
): boolean => {
  return isBound(boundOrFlight) ? boundOrFlight.itinerary.filter(isFlight).every(fn) : fn(boundOrFlight);
};

export const isByBusOnly = (
  boundOrFlight: FinnairBoundItem | FinnairDisruptedBoundItem | FinnairItineraryItemFlight
): boolean => checkBoundsOrSegmentsAllFlightsMatchesWithFn(boundOrFlight, isBus);
export const isByTrainOnly = (
  boundOrFlight: FinnairBoundItem | FinnairDisruptedBoundItem | FinnairItineraryItemFlight
): boolean => checkBoundsOrSegmentsAllFlightsMatchesWithFn(boundOrFlight, isTrain);

export function isUsingSAF(
  boundOrFlight:
    FinnairBoundItemWithLocation | FinnairBoundItem | FinnairDisruptedBoundItem | Bound | FinnairItineraryItemFlight
): boolean {
  return isBound(boundOrFlight)
    ? boundOrFlight.itinerary.some((itineraryItem) => isSegmentUsingSAF(itineraryItem))
    : isSegmentUsingSAF(boundOrFlight);
}

export function isSegmentUsingSAF(segment: FinnairItineraryItem | ItineraryItemFlight): boolean {
  if (!isFlight(segment)) {
    return false;
  }
  const opAirline = segment.operatingAirline;
  return !isBus(segment) && isFinnairNorraOrWetLease(opAirline.name, opAirline.code);
}

/**
 * Priority services aren't available if first leg is on bus and second is code share flight
 * @param itineraries
 * @returns {boolean}
 */
export const priorityServicesAvailable = (itineraries: Array<ItineraryItemFlight | ItineraryItemLayover>): boolean => {
  const legs: FinnairItineraryItemFlight[] = itineraries.filter(isFlight);
  if (legs.length <= 1) {
    return true;
  }
  const airLine = legs[1].operatingAirline;
  return !isByBusOnly(legs[0]) || isFinnairNorraOrWetLease(airLine?.name, airLine?.code);
};

export const getDapiErrorKey = (err: unknown): string => {
  const error = (err as DapiHttpErrorResponse)?.error;
  return `${error?.key ?? 'UNKNOWN_ERROR'}_${error?.errorType ?? 'UNKNOWN_TYPE'}`;
};

export const loginGenderToGenderOption = (loginGender: LoginGender): string => {
  const genderMap = {
    [LoginGender.MALE]: GENDER_OPTIONS[0].value,
    [LoginGender.FEMALE]: GENDER_OPTIONS[1].value,
  };

  return genderMap[loginGender];
};

export const corporateSalutationToGenderOption = (title: string): string | undefined => {
  switch (title) {
    case 'Mr.':
      return 'mr';
    case 'Mrs.':
    case 'Ms.':
      return 'ms';
    default:
      return undefined;
  }
};

export const finnairGenderToGenderOption = (finnairGender: FinnairGender): 'mr' | 'ms' => {
  const genderMap = {
    [FinnairGender.MALE]: GENDER_OPTIONS[0].value,
    [FinnairGender.FEMALE]: GENDER_OPTIONS[1].value,
  };

  return genderMap[finnairGender];
};

export const titleToLoginGender = (title: string): LoginGender => {
  const genderMap = {
    [Title.MR]: LoginGender.MALE,
    [Title.MS]: LoginGender.FEMALE,
    [Title.MRS]: LoginGender.FEMALE,
  };

  return genderMap[title];
};

export const titleToGender = (title: string): FinnairGender | undefined => {
  const genderMap = {
    MR: FinnairGender.MALE,
    MS: FinnairGender.FEMALE,
    MRS: FinnairGender.FEMALE,
  };

  return genderMap[title];
};

export const getCabinClassShortCode = (cabinClass: string): string => {
  const map = {
    [FinnairCabinClass.ECONOMY]: 'E',
    [FinnairCabinClass.ECOPREMIUM]: 'PE',
    [FinnairCabinClass.BUSINESS]: 'B',
    [FinnairCabinClass.FIRST]: 'F',
  };
  return map[cabinClass] || '';
};

export const getBrandNameShortCode = (brandName: string): string => {
  const name = brandName?.toUpperCase() || '';
  if (name.includes('SUPERLIGHT')) {
    return 'SUPERLIGHT';
  } else if (name.includes('LIGHT')) {
    return 'LIGHT';
  } else if (name.includes('CLASSIC')) {
    return 'CLASSIC';
  } else if (name.includes('FLEX')) {
    return 'FLEX';
  }
  return '';
};

export const adjustFareFamilyOptionHeight = (
  scrollContainer: ScrollHandleContainerComponent,
  fareHeaderSelectors: string,
  property: string
): void => {
  if (!scrollContainer) {
    return;
  }

  const fareHeaders: HTMLElement[] = Array.from<HTMLElement>(
    scrollContainer.container.nativeElement.querySelectorAll(fareHeaderSelectors)
  );
  fareHeaders.forEach((e) => {
    e.style[property] = 'auto';
  });

  const itemHeights = fareHeaders.map((e) => e.getBoundingClientRect().height);
  const maxHeight = Math.max(...itemHeights);
  fareHeaders.forEach((e) => {
    e.style[property] = `${maxHeight}px`;
  });
};

export const getTripTypeFromBookingLocationCodes = (locations: BookingLocationCodes[]): TripType => {
  if (locations.length === 1) {
    return TripType.ONEWAY;
  }

  if (
    locations.length === 2 &&
    locations[0].originLocationCode === locations[1].destinationLocationCode &&
    locations[0].destinationLocationCode === locations[1].originLocationCode
  ) {
    return TripType.RETURN;
  }

  return TripType.MULTICITY;
};

export const getLocationPairFromGlobalBookingFlights = (flights: GlobalBookingFlight[]): LocationPair[] => {
  return flights.map((flight) => {
    return {
      origin: flight.origin,
      destination: flight.destination,
    };
  });
};

export const getTripTypeFromGlobalBookingFlights = (flights: GlobalBookingFlight[]): TripType => {
  return getTripTypeFromBookingLocationCodes(
    flights.map((flight) => {
      return {
        originLocationCode: flight.origin?.locationCode,
        destinationLocationCode: flight.destination?.locationCode,
      };
    })
  );
};

export const getTripTypeFromBounds = (bounds: FinnairBoundItem[]): TripType => {
  return getTripTypeFromBookingLocationCodes(
    bounds.map((bound) => {
      return {
        originLocationCode: bound.departure.locationCode,
        destinationLocationCode: bound.arrival.locationCode,
      };
    })
  );
};

/**
 * Determine if there is cancelation with no alternative
 * @param bound
 */
export const isCancelledFlightWithNoAlternative = (bound: FinnairBoundItem): boolean =>
  [...bound.itinerary, ...(bound.disruptedBound?.disruptedBound?.itinerary ?? [])].some(
    (flight: FinnairItineraryItemFlight) =>
      flight.type === FinnairItineraryItemType.FLIGHT && flight.status === FinnairItineraryItemFlightStatus.CANCELED
  ) ?? false;

/**
 * Determine if there is disrupted bound in the booking
 * @param booking Information about the whole booking
 */
export const bookingHasDisruption = (booking: FinnairOrder): boolean =>
  (booking?.eligibilities.acknowledge?.some((eligility) => eligility.isAllowedToUse) ||
    booking?.bounds.some((bound) => isCancelledFlightWithNoAlternative(bound))) ??
  false;

/**
 * Determine if the booking has change eligibilities with waiver codes
 * @param booking Information about the whole booking
 */
export const bookingHasChangeWaiverCodes = (booking: FinnairOrder): boolean =>
  booking?.eligibilities?.change?.some((eligibility) => Boolean(eligibility.waiverCodes?.length)) ?? false;

export const bookingHasCoverService = (booking: FinnairOrder): boolean =>
  booking?.services?.included.some((includedService) => includedService.category === Category.COVER);

export const getAllFlightsFromCartOrOrder = (cartData: CartOrOrder): FinnairItineraryItemFlight[] =>
  cartData.bounds?.reduce((all, bound) => all.concat(bound.itinerary.filter(isFlight)), []);

export const isTourOperator = (hasOtherInformation: { otherInformation: FinnairOtherInformation }): boolean =>
  !!hasOtherInformation?.otherInformation?.tourOperator;

export const isNotIncludedService = (
  service: FinnairPassengerServiceSelectionItem
): service is FinnairPassengerServiceSelectionItem => {
  return !service.includedInTicketType && !service.includedInTierBenefit;
};

export const hasBusSegment = (order: FinnairOrder): boolean =>
  order.bounds.some((bound) =>
    bound.itinerary.filter(isFlight).some((i: FinnairItineraryItemFlight) => isByBusOnly(i))
  );

const isCatalogHavingService = (
  servicesToSell: FinnairServiceCatalogItemV3['services'],
  eligibilities: FinnairServiceCatalogEligibilityCategory['services'],
  bound: FinnairBoundItem
): boolean => {
  const servicesForPaxes = servicesToSell[bound.id]
    ? [servicesToSell[bound.id]]
    : bound.itinerary
        .filter(isFlight)
        .map((flight) => servicesToSell[flight.id])
        .filter(Boolean);

  return servicesForPaxes.some((servicesForPax) => {
    return Object.entries(servicesForPax).some(([paxId, paxServices]) => {
      return paxServices.length > 0 && isBoundHavingEligibilityForPax(eligibilities, bound, paxId);
    });
  });
};

const isBoundHavingEligibilityForPax = (
  eligibilities: FinnairServiceCatalogEligibilityCategory['services'],
  bound: FinnairBoundItem,
  paxId: string
): boolean => {
  const realEligibilities = eligibilities[bound.id]
    ? [eligibilities[bound.id]]
    : bound.itinerary
        .filter(isFlight)
        .map((flight) => eligibilities[flight.id])
        .filter(Boolean);
  return realEligibilities.some((eligibilityForPax) => eligibilityForPax[paxId]?.isAllowedToUse);
};

export const isBoundAirDiscount = (bound: FinnairBoundGroup): boolean =>
  Boolean(bound.originalCheapestPrice) && bound.fareFamilies.every((ff) => ff.discountReasonCode);

export const shouldWeSellForCategory = (category: Category, order: FinnairOrder, bound?: FinnairBoundItem): boolean => {
  const servicesToSell = order.serviceCatalog?.categories?.find((c) => c.category === category)?.services ?? {};
  const eligibilities =
    order.eligibilities.serviceCatalog?.categories?.find((c) => c.category === category)?.services ?? {};

  const bounds = bound ? [bound] : order.bounds;

  return bounds.some((currentBound) => isCatalogHavingService(servicesToSell, eligibilities, currentBound));
};

export const passengersMissingCategoryService = (
  category: Category,
  cartOrOrder: CartOrOrder,
  bound?: FinnairBoundItem | FinnairDisruptedBoundItem,
  useEvery = true
): boolean => {
  const eligibilities =
    cartOrOrder.eligibilities.serviceCatalog?.categories?.find((c) => c.category === category)?.services ?? {};

  const fragmentIds = isBoundBasedCategory(category)
    ? bound
      ? [bound.id]
      : cartOrOrder.bounds.map((b) => b.id)
    : (bound ? bound.itinerary.filter(isFlight) : cartOrOrder.bounds.flatMap((b) => b.itinerary).filter(isFlight)).map(
        ({ id }) => id
      );

  const eligibilitiesToUse = Object.entries(eligibilities).reduce((all, [fragmentId, eligibility]) => {
    if (fragmentIds.includes(fragmentId)) {
      all[fragmentId] = eligibility;
    }
    return all;
  }, {});

  const servicesForSegments =
    combineServices([cartOrOrder.services.included, cartOrOrder.services.unpaid])
      ?.find((service) => service.category === category)
      ?.bounds.filter((b) => (bound ? b.id === bound.id : true))
      .flatMap((b) => b.segments) ?? [];

  const predicateFunc = ([fragmentId, paxEligibilityForFlight]) => {
    const eligiblePaxIds = Object.keys(paxEligibilityForFlight)
      .filter((paxId) => paxEligibilityForFlight[paxId].isAllowedToUse)
      .sort()
      .join('');

    // for bounds we check the first segment
    const services = !isBoundBasedCategory(category)
      ? servicesForSegments.find((segment) => segment.id === fragmentId)
      : servicesForSegments[0];

    const paxIdsHavingService =
      services?.passengers
        .filter((p) => p.services.filter(isNotIncludedService).length > 0)
        .flatMap((p) => p.id)
        .sort()
        .join('') ?? '';

    return paxIdsHavingService !== eligiblePaxIds;
  };

  const noServiceForAllPassengerOnAnySegment = useEvery
    ? Object.entries(eligibilitiesToUse).every(predicateFunc)
    : Object.entries(eligibilitiesToUse).some(predicateFunc);

  return (
    Object.values(eligibilitiesToUse)
      .flatMap((s) => Object.values(s))
      .some((e) => e.isAllowedToUse) && noServiceForAllPassengerOnAnySegment
  );
};

export const servicePresentedInServices = (
  services: FinnairServices,
  category: Category,
  whereToSearchArray: Array<Exclude<keyof FinnairServices, 'servicesOrder'>> = ['included', 'pending', 'unpaid']
): boolean =>
  whereToSearchArray
    .reduce((acc, key) => [...acc, ...(services[key]?.flatMap(({ category }) => category) || [])], [])
    .includes(category);

export const checkIfAdditionalEmailExists = (
  value: string,
  additionalEmails$: Observable<string[]>
): Observable<boolean> => {
  return additionalEmails$.pipe(
    take(1),
    map((emails) => emails?.includes(value))
  );
};

export const additionalEmailValidator = (additionalEmails$: Observable<string[]>): AsyncValidatorFn => {
  return (control: AbstractControl): Observable<ValidationErrors | null> => {
    return checkIfAdditionalEmailExists(control.value, additionalEmails$).pipe(
      map((result) => {
        return result ? { duplicate: true } : null;
      })
    );
  };
};

/**
 * Splits phone number into country code and national number
 * E.g. +358401234567 -> ['FI|358', '401234567']
 * @param phoneNumberWithCountryCode
 * @param countryCodes
 */
export const splitPhoneNumber = (
  phoneNumberWithCountryCode: string,
  countryCodes: TranslatedCountryCode[]
): [string, string] => {
  if (!phoneNumberWithCountryCode) {
    return [undefined, phoneNumberWithCountryCode];
  }

  const onlyNumbers = phoneNumberWithCountryCode.replace(/\D/g, '');

  const start = 0;
  let found = false,
    len = 1,
    countryCode: TranslatedCountryCode;
  while (!found && len < 4) {
    const predicate = onlyNumbers.substring(start, start + len);
    countryCode = countryCodes.find((cc) => cc.phonePrefix === predicate);
    found = !!countryCode;
    len++;
  }
  if (found) {
    return [
      `${countryCode.countryCode}|${countryCode.phonePrefix}`,
      onlyNumbers.slice(onlyNumbers.indexOf(countryCode.phonePrefix) + countryCode.phonePrefix.length),
    ];
  }
  return [undefined, phoneNumberWithCountryCode];
};

export const getServiceStatus = (
  service: FinnairPassengerServiceSelectionItem,
  isCarrierConnect = false,
  includeAssignAtAirportStatus = false
): ServiceStatus.CONFIRMED | ServiceStatus.PENDING_PAYMENT | ServiceStatus.ASSIGN_AT_AIRPORT => {
  return service.includedInTierBenefit || service.ticketed || service.includedInTicketType
    ? isAssignAtAirportSeat(service, includeAssignAtAirportStatus, isCarrierConnect)
      ? ServiceStatus.ASSIGN_AT_AIRPORT
      : ServiceStatus.CONFIRMED
    : ServiceStatus.PENDING_PAYMENT;
};

export const isAssignAtAirportSeat = (
  service: FinnairPassengerServiceSelectionItem,
  includeAssignAtAirportStatus = false,
  isCarrierConnect = false
): boolean => {
  return (
    service.subCategory === SubCategory.SEAT &&
    includeAssignAtAirportStatus &&
    isNotIncludedService(service) &&
    !isCarrierConnect &&
    !service.seatNumber
  );
};

const getIconForService = (
  service: FinnairPassengerServiceSelectionItem,
  isCarrierConnect = false,
  includeAssignAtAirportStatus = false
): 'checkmark' | 'shopping-cart' | 'time-zone' =>
  service.includedInTierBenefit || service.ticketed || service.includedInTicketType
    ? isAssignAtAirportSeat(service, includeAssignAtAirportStatus, isCarrierConnect)
      ? 'time-zone'
      : 'checkmark'
    : 'shopping-cart';

const isSameServiceItem = (
  servItem1: FinnairPassengerServiceSelectionItem,
  servItem2: FinnairPassengerServiceSelectionItem
): boolean =>
  servItem1.variant === servItem2.variant &&
  servItem1.parameters?.baggageWeight === servItem2.parameters?.baggageWeight &&
  servItem1.totalPrice?.amount === servItem2.totalPrice?.amount &&
  servItem1.ticketed === servItem2.ticketed;

const sortIds = {
  confirmed: 2,
  pendingPayment: 1,
  availableForSell: 0,
};

export const getGroupedServices = (
  allServices: FinnairPassengerServiceSelectionItem[] = [],
  tierLevel?: string,
  variantsWeCanSell: string[] = [],
  availableServices: FinnairPassengerServiceSelectionItem[] = [],
  isCarrierConnect = false,
  includeAssignAtAirportStatus = false
): GroupedServices[] => {
  const filteredAvailableServices = availableServices.filter((service) => {
    return !allServices.some(
      (s) => s.subCategory === service.subCategory || s.subCategory.toLowerCase().includes(service.subCategory)
    );
  });

  const servicesByKey = groupBy(allServices, (service) =>
    getServiceStatus(service, isCarrierConnect, includeAssignAtAirportStatus)
  );

  const services = Object.entries(servicesByKey).map(
    ([key, innerServices]) =>
      ({
        key,
        icon: getIconForService(innerServices[0], isCarrierConnect, includeAssignAtAirportStatus),
        services: accumulateServicePrices(consolidateServiceAccumulateQuantities(innerServices)),
        showActionsButtons:
          key === ServiceStatus.PENDING_PAYMENT &&
          innerServices.some((s) => s.seatNumber || variantsWeCanSell.includes(s.variant)),
        tierLevel: tierLevel,
      }) as GroupedServices
  );

  const groupedServices =
    services.flatMap((s) => s.services.filter(isNotIncludedService)).length === 0 &&
    filteredAvailableServices.length > 0
      ? services.concat([
          {
            key: ServiceStatus.AVAILABLE_FOR_SELL,
            services: filteredAvailableServices,
            showActionsButtons: false,
            tierLevel: tierLevel,
          } as GroupedServices,
        ])
      : services;

  return groupedServices.sort((serviceA, serviceB) => sortIds[serviceB.key] - sortIds[serviceA.key]);
};

const consolidateServiceAccumulateQuantities = (
  services: FinnairPassengerServiceSelectionItem[]
): FinnairPassengerServiceSelectionItem[] => {
  return services.reduce((consolidatedServices, service) => {
    const consolidatedService = consolidatedServices.find((s) => isSameServiceItem(service, s));
    if (consolidatedService) {
      consolidatedService.quantity += service.quantity;
    } else {
      consolidatedServices.push({ ...service });
    }
    return consolidatedServices;
  }, []);
};

const accumulateServicePrices = (
  services: FinnairPassengerServiceSelectionItem[]
): FinnairPassengerServiceSelectionItem[] => {
  return services.reduce((consolidatedPriceServices, service) => {
    const consolidatedServiceWithPrice =
      service.includedInTicketType || service.includedInTierBenefit
        ? service
        : {
            ...service,
            totalPrice: getServicePrice(service.unitPrice, service.quantity),
          };
    consolidatedPriceServices.push(consolidatedServiceWithPrice);
    return consolidatedPriceServices;
  }, []);
};

const getServicePrice = (price: FinnairAmount, quantity: number) => {
  return price?.amount
    ? {
        ...price,
        amount: (parseFloat(price.amount) * quantity).toFixed(2),
      }
    : price;
};

const mapPassenger = (bookingPassenger: FinnairPassengerItem): FinnairPassengerServiceItem => ({
  id: bookingPassenger.id,
  firstName: bookingPassenger.firstName,
  lastName: bookingPassenger.lastName,
  services: [],
});

const fulfillServicesBoundWithPassenger = (
  bookingBound: FinnairBoundItem,
  service: FinnairServiceItem,
  bookingPassengers: FinnairPassengerItem[]
): FinnairServiceBoundItem => {
  const existedServiceBound = service.bounds.find((bound) => bound.id == bookingBound.id);

  if (existedServiceBound) {
    return {
      ...existedServiceBound,
      segments: bookingBound.itinerary.filter(isFlight).map((itineraryItem) => {
        const foundItem = existedServiceBound.segments.find((segment) => segment.id === itineraryItem.id);

        if (foundItem) {
          return {
            ...foundItem,
            passengers: bookingPassengers.map((bookingPassenger) => {
              const existingPassenger = foundItem.passengers.find((passenger) => passenger.id === bookingPassenger.id);

              return existingPassenger || mapPassenger(bookingPassenger);
            }),
          };
        }
        return {
          arrival: itineraryItem.arrival,
          departure: itineraryItem.departure,
          id: itineraryItem.id,
          passengers: bookingPassengers.map(mapPassenger),
          quantity: 1,
          totalPrice: {},
        } as FinnairServiceSegmentItem;
      }),
    } as FinnairServiceBoundItem;
  } else {
    return {
      id: bookingBound.id,
      totalPrice: {},
      segments: bookingBound.itinerary.filter(isFlight).map((itineraryItem) => ({
        arrival: itineraryItem.arrival,
        departure: itineraryItem.departure,
        id: itineraryItem.id,
        passengers: bookingPassengers.map(mapPassenger),
        quantity: 1,
        totalPrice: {},
      })),
    } as FinnairServiceBoundItem;
  }
};

const upsellForCategory = (
  category: Category,
  bound: FinnairBoundItem,
  catalog: FinnairServiceCatalogV3 | undefined,
  categoriesWithUpsell: Category[] = [
    Category.CABIN_BAGGAGE,
    Category.BAGGAGE,
    Category.MEAL,
    Category.SEAT,
    Category.WIFI,
    Category.SPORT,
    Category.LOUNGE,
    Category.PET,
    Category.PRIORITY,
  ]
) => {
  const catalogForCategory = catalog?.categories.find((c) => c.category === category);
  const fragmentIds = isJourneyBasedCategory(category)
    ? [CustomServiceType.JOURNEY]
    : isBoundBasedCategory(category)
      ? [bound.id]
      : bound.itinerary.filter(isFlight).map((flight) => flight.id);
  return (
    categoriesWithUpsell.includes(category) &&
    fragmentIds.some(
      (fragmentId) =>
        !!catalogForCategory?.services[fragmentId] ||
        (catalogForCategory?.category === Category.SEAT && !!catalogForCategory?.lowestPrice[fragmentId])
    )
  );
};

const createEmptyServiceFromCatalog = (
  category: Category,
  bound: FinnairBoundItem,
  catalogForCategory: FinnairServiceCatalogItemV3
): FinnairServiceItem => {
  return {
    ...catalogForCategory,
    bounds: [{ id: bound.id, segments: [], totalPrice: null, quantity: 0 }],
    category,
    quantity: 0,
    title: catalogForCategory.translations.title,
  };
};

export const getLowestPrice = (
  lowestPrice: FinnairServiceCatalogItemV3['lowestPrice'],
  fragmentIds: string[]
): FinnairLowestPrice | null => {
  if (!lowestPrice) {
    return null;
  }

  return fragmentIds.reduce<FinnairLowestPrice | null>((cheapest, fragmentId) => {
    const lowestPricesForPaxes = lowestPrice[fragmentId];

    if (!lowestPricesForPaxes || isEmptyObject(lowestPricesForPaxes)) {
      return cheapest;
    }

    const fragmentLowestPrice = Object.values(lowestPricesForPaxes).reduce<FinnairLowestPrice | null>(
      (innerCheapest, currentPrice) => {
        return !innerCheapest || +currentPrice.money.amount < +innerCheapest.money.amount
          ? currentPrice
          : innerCheapest;
      },
      null
    );

    return !cheapest || (fragmentLowestPrice && +fragmentLowestPrice.money.amount < +cheapest.money.amount)
      ? fragmentLowestPrice
      : cheapest;
  }, null);
};

const getExpandUpsellService = (
  catalogForCategory: FinnairServiceCatalogItemV3,
  cartOrOrder: CartOrOrder,
  bound: FinnairBoundItem
): boolean => {
  if (![Category.SEAT, Category.CABIN_BAGGAGE, Category.BAGGAGE].includes(catalogForCategory.category)) {
    return false;
  }

  const seatMissing = passengersMissingCategoryService(Category.SEAT, cartOrOrder, bound);
  const cabinBagMissing = passengersMissingCategoryService(Category.CABIN_BAGGAGE, cartOrOrder, bound);
  const bagMissing = passengersMissingCategoryService(Category.BAGGAGE, cartOrOrder, bound);

  /**
   * If an ancillary is unpaid, the user has added this during the manage-booking or check-in flow
   * and we want to show the upsell expanded to show this added service.
   */
  const seatUnpaid = servicePresentedInServices(cartOrOrder.services, Category.SEAT, ['unpaid']);
  const cabinBagUnpaid = servicePresentedInServices(cartOrOrder.services, Category.CABIN_BAGGAGE, ['unpaid']);
  const bagUnpaid = servicePresentedInServices(cartOrOrder.services, Category.BAGGAGE, ['unpaid']);

  switch (catalogForCategory.category) {
    case Category.SEAT:
      return seatMissing || seatUnpaid;
    case Category.CABIN_BAGGAGE:
      return (cabinBagMissing || cabinBagUnpaid) && !seatMissing;
    case Category.BAGGAGE:
      return (bagMissing || bagUnpaid) && !cabinBagMissing && !seatMissing;
    default:
      return false;
  }
};

const getUpsell = (
  cartOrOrder: CartOrOrder,
  catalogForCategory: FinnairServiceCatalogItemV3 | undefined,
  service: FinnairServiceItem,
  bound: FinnairBoundItem,
  shouldNotShowAddMoreBtn: boolean,
  isUpdating: boolean
): Upsell | null => {
  if (!catalogForCategory) {
    return null;
  }

  const fragmentIds = isJourneyBasedCategory(catalogForCategory.category)
    ? [CustomServiceType.JOURNEY]
    : isBoundBasedCategory(catalogForCategory.category)
      ? [bound.id]
      : bound.itinerary.filter(isFlight).map((flight) => flight.id);
  const lowestPrice = getLowestPrice(catalogForCategory.lowestPrice, fragmentIds);

  if (!lowestPrice?.money || +lowestPrice?.money.amount === 0) {
    return null;
  }

  const hasServicesForCategory =
    combineServices([cartOrOrder.services.included, cartOrOrder.services.unpaid])
      ?.find((service) => service.category === catalogForCategory.category)
      ?.bounds.filter((b) => bound.id === b.id)
      .flatMap((b) => b.segments)
      .flatMap((segment) => segment.passengers).length > 0;

  const isShowMoreBtn =
    hasServicesForCategory &&
    !shouldNotShowAddMoreBtn &&
    [Category.BAGGAGE, Category.SPORT].includes(service.category) &&
    servicePresentedInServices(cartOrOrder.services, service.category, ['included', 'unpaid']);

  /**
   * Custom hasPlusTierBenefit tier logic for LOUNGE
   */
  const hasPlusTierBenefit =
    service.category === Category.LOUNGE &&
    service.bounds
      .flatMap((bound) => bound.segments.flatMap((segment) => segment.passengers))
      .some((passenger) => {
        return (
          getPassengerTierLevel(passenger.id, cartOrOrder.passengers)?.toLowerCase().includes('gold') &&
          passenger.services.some((s) => s.includedInTierBenefit)
        );
      });

  return {
    articleKey: `MMB.fragments.upsell.${service.category}.allPax.url`,
    expand: getExpandUpsellService(catalogForCategory, cartOrOrder, bound),
    enabled: !hasServicesForCategory,
    lowestPrice: lowestPrice.money,
    isShowMoreBtn,
    updatingService: isUpdating,
    hasPlusTierBenefit,
  };
};

const isBoundFlownWithTimeDifference = (bound: FinnairBoundItem, diffInHours = 0) => {
  const flights = bound.itinerary.filter(isFlight);
  return (
    TzDate.now().hoursTo(TzDate.fromMillis(Date.parse(flights[flights.length - 1]?.arrival.dateTime))) - diffInHours <=
    0
  );
};

export const shouldWeUpsell = (
  category: Category,
  bound: FinnairBoundItem,
  catalog: FinnairServiceCatalogV3,
  categoriesWithUpsell?: Category[]
): boolean => {
  return upsellForCategory(category, bound, catalog, categoriesWithUpsell) && !isBoundFlownWithTimeDifference(bound);
};

const defaultServicesOrder = [
  Category.CABIN_BAGGAGE,
  Category.SEAT,
  Category.BAGGAGE,
  Category.MEAL,
  Category.SPORT,
  Category.WIFI,
  Category.PET,
  Category.LOUNGE,
  Category.PRIORITY,
  Category.COVER,
  Category.OTHER,
  Category.SPECIAL,
  Category.SPECIAL_NEED,
  Category.TRAVEL_COMFORT,
  Category.FIREARM,
  Category.MEDIC,
  Category.CHILD,
  Category.SAF,
];

export const findSegmentById = (segmentId: string, bound: FinnairBoundItem): FinnairItineraryItemFlight | undefined => {
  return bound.itinerary.filter(isFlight).find(({ id }) => id === segmentId);
};

export const findPassengerById = (paxId: string, order: FinnairOrder): FinnairPassengerItem | undefined => {
  return order.passengers.find((p) => p.id === paxId);
};

export const createEmptySeatServices = (
  servicesToShow: FinnairServiceItemWithIcon[],
  bound: FinnairBoundItem,
  requiredPassengerIds: string[] = [],
  order: FinnairOrder,
  seatsTitle: string
): FinnairServiceItemWithIcon[] => {
  const allSegmentIds: string[] = bound.itinerary
    .filter((i) => i.type === FinnairItineraryItemType.FLIGHT)
    .map((i) => (i as ItineraryItemFlight).id);

  const seats = servicesToShow.find((service) => service.category === Category.SEAT);
  const seatServicesBound = seats?.bounds?.find((b) => b.id === bound.id);
  const fakeEmptySeatService = [
    {
      ticketed: true,
      seatNumber: null,
      subCategory: SubCategory.SEAT,
    },
  ];
  if (!seats || !seatServicesBound) {
    // create seat empty services for whole bound
    return [
      ...servicesToShow,
      {
        bounds: [
          {
            id: bound.id,
            segments: allSegmentIds.map((segmentId) => {
              const segment = findSegmentById(segmentId, bound);
              return {
                id: segmentId,
                arrival: segment.arrival,
                departure: segment.departure,
                passengers: requiredPassengerIds
                  .map((paxId) => {
                    const pax = findPassengerById(paxId, order);
                    return pax
                      ? {
                          id: paxId,
                          firstName: pax.firstName,
                          lastName: pax.lastName,
                          services: fakeEmptySeatService,
                        }
                      : null;
                  })
                  .filter((pax) => pax !== null),
                quantity: requiredPassengerIds.length,
                totalPrice: {},
              } as FinnairServiceSegmentItem;
            }),
            totalPrice: {},
          } as FinnairServiceBoundItem,
        ],
        category: Category.SEAT,
        quantity: allSegmentIds.length * requiredPassengerIds.length,
        title: seatsTitle,
        icon: getIconForServiceCategory(Category.SEAT),
      },
    ].sort(
      (a, b) => order.services.servicesOrder.indexOf(a.category) - order.services.servicesOrder.indexOf(b.category)
    );
  } else if (seats && seatServicesBound) {
    // fill in segments items with empty seats for all passengers
    const missingSegmentIds = allSegmentIds.filter(
      (id) => !seatServicesBound.segments.find((segment) => segment.id === id)
    );
    seatServicesBound.segments = [
      ...seatServicesBound.segments,
      ...missingSegmentIds
        .map((segmentId) => {
          const segment = findSegmentById(segmentId, bound);
          if (!segment) {
            return null;
          }
          return {
            id: segmentId,
            arrival: segment.arrival,
            departure: segment.departure,
            passengers: requiredPassengerIds
              .map((paxId) => {
                const pax = findPassengerById(paxId, order);
                return pax
                  ? {
                      id: paxId,
                      firstName: pax.firstName,
                      lastName: pax.lastName,
                      services: fakeEmptySeatService,
                    }
                  : null;
              })
              .filter((pax) => pax !== null),
            quantity: requiredPassengerIds.length,
            totalPrice: {},
          } as FinnairServiceSegmentItem;
        })
        .filter(Boolean),
    ].sort((segmentA, segmentB) => (allSegmentIds.indexOf(segmentA.id) < allSegmentIds.indexOf(segmentB.id) ? -1 : 1));

    // fill in missing passengers items (if passenger not found in segment)
    if (seatServicesBound?.segments) {
      seatServicesBound.segments = seatServicesBound?.segments?.map((segment) => {
        const missingPassengers = requiredPassengerIds
          .filter((id) => !segment.passengers?.some((passenger) => passenger.id === id))
          .map((paxId) => {
            const pax = findPassengerById(paxId, order);
            return pax
              ? {
                  id: paxId,
                  firstName: pax.firstName,
                  lastName: pax.lastName,
                  services: fakeEmptySeatService,
                }
              : null;
          })
          .filter((pax) => pax !== null) as FinnairPassengerServiceItem[];

        return {
          ...segment,
          passengers: [...segment.passengers, ...missingPassengers],
        };
      });
    }
    // fill in service items (if passenger.services has no seats)
    let segmentToPaxIdsMap = new Map<string, string[]>();
    if (seatServicesBound?.segments) {
      segmentToPaxIdsMap = seatServicesBound.segments.reduce((segmentMissingPaxIds, segment) => {
        const paxIdsMissingSeatServices = segment.passengers
          .filter((p) => p.services.length === 0)
          .map((p) => p.id)
          .filter((paxId) => requiredPassengerIds.includes(paxId));

        if (paxIdsMissingSeatServices.length > 0) {
          segmentMissingPaxIds.set(segment.id, paxIdsMissingSeatServices);
        }

        return segmentMissingPaxIds;
      }, segmentToPaxIdsMap);
    }
    const filledInSeatServices = {
      ...seats,
      bounds: seats.bounds.map((b) => {
        return b.id === bound.id
          ? {
              ...seatServicesBound,
              segments: seatServicesBound.segments.map((segment) => {
                return {
                  ...segment,
                  passengers: segment.passengers.map((passenger) => {
                    return {
                      ...passenger,
                      services: segmentToPaxIdsMap.get(segment.id)?.includes(passenger.id)
                        ? [
                            {
                              ticketed: true,
                              seatNumber: null,
                            },
                          ]
                        : passenger.services,
                    };
                  }),
                };
              }),
            }
          : b;
      }),
    };
    return servicesToShow.map((service) => {
      return service.category === Category.SEAT ? filledInSeatServices : service;
    }) as FinnairServiceItemWithIcon[];
  }
};

const EXCLUDE_FROM_SUMMARY_VARIANTS = ['SH_ECO_MEAL_DEFAULT_1', 'SH_ECO_MEAL_DEFAULT_2', 'SH_ECO_DRINK_DEFAULT_1'];

export const calculateIsIncludedAndQuantity = (
  service: FinnairServiceItemWithUpsell,
  currentBound: FinnairBoundItem,
  showPendingServices: boolean
): { anyIncluded: boolean; totalQuantity: number } => {
  const allSelectedItems = service.bounds
    .filter((bound) => bound.id === currentBound.id)
    .flatMap((bound) => bound.segments)
    .flatMap((flight) => flight.passengers)
    .flatMap((pax) => pax.services)
    .filter(({ variant }) => !EXCLUDE_FROM_SUMMARY_VARIANTS.includes(variant))
    .filter((s) => getServiceStatus(s) === ServiceStatus.CONFIRMED || showPendingServices);

  const totalQuantity = allSelectedItems
    .filter((paxService) => isNotIncludedService(paxService))
    .flatMap((paxService) => paxService.quantity)
    .reduce((total, count) => {
      total += count;
      return total;
    }, 0);

  const anyIncluded = allSelectedItems.some(
    (paxService) => paxService.includedInTicketType || paxService.includedInTierBenefit
  );

  return { anyIncluded, totalQuantity };
};

const hasConfirmedSelection = (paxServiceItem: FinnairPassengerServiceSelectionItem): boolean =>
  !EXCLUDE_FROM_SUMMARY_VARIANTS.includes(paxServiceItem.variant) &&
  getServiceStatus(paxServiceItem) === ServiceStatus.CONFIRMED;

export const fragmentIdsWithAnyPassengerMissingService = (
  serviceBounds: FinnairServiceBoundItem[],
  bound: FinnairBoundItem,
  passengerIds: string[]
): string[] => {
  const flightIds = bound.itinerary.filter(isFlight).map(({ id }) => id);

  if (passengerIds.length === 0) {
    return flightIds;
  }

  return flightIds.filter((flightId) => {
    const servedPassengerIds = new Set(
      serviceBounds
        .flatMap(({ segments }) => segments ?? [])
        .filter(({ id }) => id === flightId)
        .flatMap(({ passengers }) => passengers)
        .filter(({ services }) => (services ?? []).some(hasConfirmedSelection))
        .map(({ id }) => id)
    );

    return passengerIds.some((passengerId) => !servedPassengerIds.has(passengerId));
  });
};

export const getServicesForBoundWithUpsell = (
  cartOrOrder: CartOrOrder,
  showUpsell: boolean,
  servicesToShow: FinnairServiceItem[],
  servicesOrder: Category[] = defaultServicesOrder,
  bound: FinnairBoundItem,
  catalog: FinnairServiceCatalogV3 | undefined,
  categoryStatusMap: Partial<Record<Category, OrderPartStatus>>,
  categoriesWithUpsell?: Category[]
): FinnairServiceItemWithUpsell[] => {
  const unpaidServices: Record<Category, number> = (cartOrOrder?.services?.unpaid ?? []).reduce(
    (acc, service) => {
      const boundQuantity = service.bounds.find((b) => b.id === bound.id)?.quantity ?? 0;
      acc[service.category] = (acc[service.category] || 0) + boundQuantity;
      return acc;
    },
    {} as Record<Category, number>
  );

  const passengerIdsConsideredForQuota = (cartOrOrder?.passengers ?? [])
    .filter(({ passengerTypeCode }) => passengerTypeCode !== FinnairPassengerCode.INF)
    .map(({ id }) => id);

  if (!showUpsell) {
    return servicesToShow
      .map((service) => {
        const serviceBoundsForBound = service.bounds.filter(({ id }) => id === bound.id);

        return {
          ...service,
          bounds: serviceBoundsForBound,
          unpaidQuantity: unpaidServices[service.category] || 0,
          categoryLevelQuotaCount: getCategoryLevelQuotaCount(
            catalog,
            service.category,
            bound,
            fragmentIdsWithAnyPassengerMissingService(serviceBoundsForBound, bound, passengerIdsConsideredForQuota)
          ),
        };
      })
      .filter((s) => s.bounds.length > 0)
      .sort((a, b) => servicesOrder.indexOf(a.category) - servicesOrder.indexOf(b.category));
  }

  return servicesOrder
    .filter((category) => {
      const showService = servicesToShow.some(
        (service) => service.category === category && service.bounds.some((b) => b.id === bound.id)
      );
      return (
        showService ||
        shouldWeUpsell(category, bound, catalog, categoriesWithUpsell) ||
        category === Category.SPECIAL_NEED
      );
    })
    .map((category) => {
      const shouldWeUpsellForThisCategory = shouldWeUpsell(category, bound, catalog, categoriesWithUpsell);
      const catalogForCategory = shouldWeUpsellForThisCategory
        ? catalog.categories.find((item) => item.category === category)
        : null;
      const existingService =
        servicesToShow.find((s) => s.category === category) ??
        (catalogForCategory ? createEmptyServiceFromCatalog(category, bound, catalogForCategory) : null);

      const bounds: FinnairServiceBoundItem[] = existingService
        ? [fulfillServicesBoundWithPassenger(bound, existingService, cartOrOrder.passengers)]
        : (existingService?.bounds ?? []);

      if (existingService) {
        const shouldNotShowAddMoreBtn = bounds.some((b) =>
          b.segments.some((s) => s.passengers.some((p) => p.services.length === 0))
        );
        const serviceCategory = existingService.category;
        const isUpdating = categoryStatusMap[serviceCategory] === OrderPartStatus.UPDATING;
        const upsell = shouldWeUpsellForThisCategory
          ? getUpsell(cartOrOrder, catalogForCategory, existingService, bound, shouldNotShowAddMoreBtn, isUpdating)
          : null;

        return {
          ...existingService,
          bounds,
          upsell,
          unpaidQuantity: unpaidServices[category] || 0,
          categoryLevelQuotaCount: getCategoryLevelQuotaCount(
            catalog,
            category,
            bound,
            fragmentIdsWithAnyPassengerMissingService(bounds, bound, passengerIdsConsideredForQuota)
          ),
        };
      }

      if (
        category === Category.SPECIAL_NEED &&
        !isBoundFlownWithTimeDifference(bound, 48) &&
        bound.itinerary
          .filter(isFlight)
          .some((flight) => isFinnairNorraOrWetLease(flight.operatingAirline?.name, flight.operatingAirline?.code))
      ) {
        return {
          bounds: [{ id: bound.id, segments: [], totalPrice: null, quantity: 0 }],
          category,
          quantity: 0,
          title: '',
          unpaidQuantity: unpaidServices[category] || 0,
          upsell: {
            enabled: true,
            lowestPrice: null,
            isShowMoreBtn: servicePresentedInServices(cartOrOrder.services, category, ['included', 'unpaid']),
            articleKey: `MMB.fragments.upsell.${category}.allPax.url`,
            expand: false,
            updatingService: false,
            hasPlusTierBenefit: false,
          },
        };
      }

      return null;
    })
    .filter(Boolean)
    .filter((s) => s.bounds.length > 0);
};

export const isBoundAPISRequired = (order: FinnairOrder, bound: FinnairBoundItem): boolean => {
  return !!order.eligibilities?.checkIn?.find(
    (eligibility) => eligibility.id === bound.id && eligibility.isApisRequired
  );
};

export const isOrder = (order: FinnairCart | FinnairOrder): boolean => {
  return order.id.length === 6;
};

export const isFlexFareFamilyBound = (bound: FinnairBoundItem): boolean =>
  bound.fareFamily?.code?.includes('FLEX') ?? false;

export const locationPairIncludesTrainDestination = (locations: LocationPair): boolean => {
  return ['origin', 'destination'].some((direction) => {
    return RAIL_AND_FLY_LOCATION_CODES.includes(locations?.[direction]?.locationCode);
  });
};

export const hasPartiallyUNBoundInBooking = (order: FinnairOrder): boolean => {
  return order.bounds.some((bound) => isPartiallyUNBound(bound)) ?? false;
};

export const isPartiallyUNBound = (bound: FinnairBoundItem): boolean => {
  const disruptedItinerary = bound.disruptedBound?.disruptedBound?.itinerary;
  if (!disruptedItinerary || disruptedItinerary.length === 0) {
    return false;
  }
  const disruptionBoundFlightCount = disruptedItinerary.filter(isFlight).length;
  const cancelledFlightsCount = disruptedItinerary
    .filter(isFlight)
    .filter(({ status }) => status === FinnairItineraryItemFlightStatus.CANCELED).length;
  return cancelledFlightsCount > 0 && disruptionBoundFlightCount > cancelledFlightsCount;
};

export const isBusinessClassFlightWithIsolatedSeats = (
  airlineCode?: string,
  aircraftCode?: string,
  cabinClass?: string
): boolean =>
  airlineCode === Airline.FINNAIR &&
  ['33', '35'].some((prefix) => aircraftCode?.startsWith(prefix)) &&
  cabinClass === FinnairCabinClass.BUSINESS;

export const shouldShowKidsInBusinessNotification = (
  bounds: FinnairBoundItem[],
  passengers: FinnairPassengerItem[] | PaxDetailsFormData[]
): boolean => {
  if (
    !bounds?.some((b) =>
      b.itinerary
        .filter(isFlight)
        .some((i) => isBusinessClassFlightWithIsolatedSeats(i.operatingAirline?.code, i.aircraft?.code, i.cabinClass))
    )
  ) {
    return false;
  }

  return passengers.some((p) => ('passengerTypeCode' in p ? p.passengerTypeCode : p.type) === FinnairPassengerCode.CHD);
};
